import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import * as THREE from 'three';
import '../styles/FloorPlanViewer.css';
import {
  getRoomType,
  findMostFrequentElement,
  drawLabel,
  filterObjectsByHeight,
  findFloorY,
  metersToFeetInches,
} from '../utils/floorPlanUtils';
import { findExteriorDoors } from '../utils/exteriorDoorUtils';
import { findHallways } from '../utils/hallwayUtils';
import { exportFloorplanToPDF } from '../utils/pdfExport';
import { isPointNearDoor, drawDoors } from '../utils/doorUtils';
import { HIGHLIGHT_COLORS } from '../constants/highlightColors';
import { VANITY_DIMENSIONS } from '@shared-lib/vanity-assembly/constants';
import { isPointInsidePolygon } from '@shared-lib/geometry/pointInPolygon';
import {
  getObjectType,
  getObjectColor,
  drawObjectShape,
} from '../utils/objectDrawingUtils';
import {
  calculateRoomMeasurementsFromPolygon,
  drawMeasurements,
} from '../utils/measurementUtils';
import { snapElementToWall } from '../utils/wallSnappingUtils';
import { computeClickedRoomWallSpan } from '../utils/wholeWallDemo';
import { getWallAxisXZ } from '../utils/wallGeometryUtils';
import {
  snapWallEndpointToWall,
  snapWallPlacementToWall,
} from '../utils/snapToWall';
import {
  WALL_THICKNESS,
  getCeilingHeightAtPoint,
} from '../utils/wallCreationUtils';
import {
  isWallMountedOpening,
  isDemoedOpening,
} from '../utils/parentWallHelpers';
import {
  drawRoomPolygonPreview,
  drawSnapIndicator,
  drawVertexDeleteOverlay,
  drawVertexAddOverlay,
  drawVertexMoveOverlay,
} from '../utils/roomDrawingUtils';
import {
  calculateRoomMeasurementsV2,
  calculateAllWallMeasurements,
  drawMeasurementsV2,
  layoutMeasurements,
  hitTestMeasurement,
  measurementInvolvesAnyObject,
} from '../utils/measurementUtilsV2';
import { computeMeasurementAwareFitScale } from '../utils/floorPlanFitScale';
import {
  updateCanvasSize,
  setupResizeObserver,
  clearCanvas,
  setupCanvasTransform,
  restoreCanvasTransform,
} from '../utils/canvasUtils';
import { computeRoomTakeOffs } from '../utils/roomTakeOffs';
import {
  apply3DRotationTo2D,
  calculateMiterPoint,
  isPointNearWall,
  isPointNearWindow,
  isPointNearOpening,
  isPointInObjectBounds,
  isPointInSinkBounds,
  screenToWorldCoordinates,
  worldToScreenCoordinates,
} from '../utils/geometryUtils';
import {
  drawWalls,
  drawContextWalls,
  drawWallNormals,
  drawCeilingCoverage,
  drawRoomPolygonOutline,
  drawWallsFromRoomPolygon,
  drawWindows,
  drawDoorsWithRotation,
  drawOpenings,
  drawObjects,
  drawObject,
  drawRoomLabels,
  drawAxes,
  matchesOpeningSelection,
} from '../utils/elementDrawingUtils';
import {
  buildOpeningLookup,
  resolveRoomOpening,
} from '../utils/openingSyncUtils';
import {
  detectClickedElement,
  createPanHandlers,
} from '../utils/interactionUtils';
import {
  isSink,
  getSinkType,
  createSinkObject,
  validateSinkPlacement,
  getSinksOnCountertop,
} from '../utils/sinkHelpers';
import {
  drawResizeHandles,
  hitTestHandles,
  calculateResize,
  getHandleCursor,
  isPointInObject,
} from '../utils/resizeHandlesUtils';
import {
  drawWallHandles,
  hitTestWallHandles,
  getWallHandleCursor,
  getWallEndPoints,
} from '../utils/wallHandlesUtils';
import { isPolygonWall, scalePolygonCorners } from '../utils/polygonWallUtils';
import {
  canHaveCountertopOnTop,
  isElectrical,
} from '../utils/objectCategoryHelper';
import { isObjectType } from '../utils/objectTypeUtils';
import { shouldShowObjectInLayer } from '../utils/objectVisibility';
import {
  ELECTRICAL_DEVICES,
  isElectricalDeviceType,
} from '../utils/electricalDevices';
import { createSinkFaucet } from '@shared-lib/vanity-assembly/createSinkFaucet';
import { checkPlacement, snapToValidPosition } from '@shared-lib/collision';
import { filterCollisions, toSkipChecks } from '../utils/constraintFilter';
import { collidingObjectIds } from '../utils/detectRoomCollisions';
import { drawDragClearanceZones } from '../utils/clearanceDrawing';
import { drawCollisionDebugOverlay } from '../utils/collisionDebugOverlay';
import { getDefaultGLBModel } from '../services/glbModelService';
import {
  drawElectricalOverlay,
  collectAllObjects,
  collectAllElectricalData,
  WIRE_HANDLE_RADIUS_PX,
} from '../utils/electricalDrawingUtils';
import ParentChildSystem from '../utils/parentChildSystem';
import { useFloorplanRenderer } from './2d/useFloorplanRenderer';
import CalloutTextEditor from './2d/CalloutTextEditor';
import { useFloorplanGestures } from './2d/useFloorplanGestures';
import {
  filterDataToSelectedRoom,
  itemIsWall,
  getElementsWithRoomContext,
} from './2d/floorplanDataView';
import { roomGroupMembers } from '../utils/roomGroupUtils';
import { buildFloorplanContracts } from './2d/floorplanContracts';
import { useCoarsePointer } from '../hooks/useCoarsePointer';
import useRegionMaterialTypes from '../hooks/useRegionMaterialTypes';
import { installFloorPlanControls } from '../utils/floorPlanControlsBridge';
import { fixedPinAt } from '../utils/fixedCabinetMarker';
import { calloutAtPoint } from '../utils/calloutPlan';
import { runAtPoint } from '../utils/electricalRuns';
import {
  HIGHLIGHT_FADE_DELAY_MS,
  HIGHLIGHT_FADE_DURATION_MS,
} from '../utils/highlightFade';

// Wall-mounted openings that also accept canvas-drag resize (plain
// openings + demoed walls — doors and windows use the panel dimension
// editors). When eligible, only the two end handles (B = right, D =
// left along wall axis) are drawn / hit-tested; height and depth are
// pinned to the wall so the four corner + top/bottom handles would
// just let the user push center perpendicular off the wall.
const canvasResizeHandlesFor = (item) =>
  item && isWallMountedOpening(item) && item.category?.opening
    ? new Set(['B', 'D'])
    : null;

// Default sizes for different object types (in meters)
const getDefaultSize = (type) => {
  // Electrical devices take their size from the one catalog.
  if (isElectricalDeviceType(type)) {
    return { ...ELECTRICAL_DEVICES[type.toLowerCase()].dimensions };
  }
  switch (type) {
    case 'table':
      return { x: 1.22, y: 0.76, z: 1.22 }; // 4' x 4' table, 30" height
    case 'chair':
      return { x: 0.45, y: 0.9, z: 0.45 }; // 18" x 18" seat, 36" height
    case 'sink':
      return { x: 0.559, y: 0.2, z: 0.457 }; // 22" x 18" sink
    case 'bathroom-sink-faucet':
      return { x: 0.1, y: 0.25, z: 0.1 }; // 4" x 10" x 4" bathroom faucet
    case 'toilet':
      return { x: 0.4, y: 0.76, z: 0.7 }; // Standard toilet dimensions
    case 'bathtub':
      return { x: 1.52, y: 0.6, z: 0.76 }; // 5' x 30" bathtub
    case 'tub-shower':
      return { x: 1.524, y: 0.6, z: 0.762 }; // 60" x 30" alcove tub-shower
    case 'shower':
      return { x: 0.91, y: 2.1, z: 0.91 }; // 3' x 3' shower
    case 'stove':
    case 'range':
      return { x: 0.76, y: 0.9, z: 0.61 }; // 30" x 24" range/stove
    case 'fridge':
      return { x: 0.91, y: 1.8, z: 0.76 }; // 36" x 30" fridge
    case 'dishwasher':
      return { x: 0.61, y: 0.85, z: 0.61 }; // 24" dishwasher
    case 'washerDryer':
      return { x: 0.69, y: 1.1, z: 0.74 }; // Standard washer/dryer
    case 'storage':
      return { x: 1.0, y: 2.0, z: 0.5 }; // Storage cabinet
    case 'stairs':
      return { x: 0.91, y: 3.0, z: 3.0 }; // Standard staircase
    case 'door':
      return { x: 0.91, y: 2.1, z: 0.05 }; // 3' wide door, 7' height, 2" thick
    case 'window':
      return { x: 1.2, y: 1.2, z: 0.1 }; // 4' wide window, 4' height
    case 'opening':
      return { x: 1.2, y: 2.1, z: 0.05 }; // 4' wide opening, 7' height
    case 'demoedWall':
      return { x: 0.91, y: 2.4, z: 0.05 }; // 3' wide, full 8' wall height
    case 'wholeWall':
      return { x: 0.91, y: 2.4, z: 0.05 }; // same as demoedWall — overridden by structure wall dims on snap

    // Additional furniture
    case 'sofa':
      return { x: 2.13, y: 0.9, z: 0.91 }; // 7' x 3' sofa
    case 'bed':
      return { x: 1.52, y: 0.6, z: 2.03 }; // Queen bed: 5' x 6'8"
    case 'dresser':
      return { x: 1.22, y: 1.2, z: 0.5 }; // 4' wide dresser
    case 'bookshelf':
      return { x: 0.91, y: 1.83, z: 0.3 }; // 3' x 6' bookshelf
    case 'cabinet':
      return { x: 0.91, y: 0.9, z: 0.61 }; // Standard cabinet

    // Additional bathroom — match createVanity's single-sink default so the
    // placement preview matches the actual fixture footprint.
    case 'vanity':
      return {
        x: VANITY_DIMENSIONS.singleSink.width,
        y: VANITY_DIMENSIONS.singleSink.height,
        z: VANITY_DIMENSIONS.singleSink.depth,
      };

    // Additional kitchen
    case 'oven':
    case 'wall-oven':
      return { x: 0.61, y: 0.85, z: 0.61 }; // Built-in oven

    // Additional appliances
    case 'television':
      return { x: 1.27, y: 0.71, z: 0.05 }; // 50" TV
    case 'fireplace':
      return { x: 1.52, y: 1.22, z: 0.46 }; // 5' x 4' fireplace

    case 'wall-light':
      return { x: 0.3, y: 0.3, z: 0.15 }; // 12" x 12" wall sconce, 6" depth
    case 'ceiling-light':
      return { x: 0.1, y: 0.05, z: 0.1 }; // Default puck light: 4" dia x 2" height
    case 'mirror':
      return { x: 0.61, y: 0.91, z: 0.05 }; // 24" x 36" x 2" mirror

    // Cabinet types
    case 'lower-cabinet':
      return { x: 0.61, y: 0.91, z: 0.61 }; // 24" x 36" x 24" lower cabinet
    case 'upper-cabinet':
      return { x: 0.61, y: 0.76, z: 0.3 }; // 24" x 30" x 12" upper cabinet
    case 'countertop':
      return { x: 0.61, y: 0.038, z: 0.635 }; // 24" x 1.5" x 25" countertop
    case 'storage-furniture':
      return { x: 0.8, y: 1.8, z: 0.4 }; // Movable storage furniture

    default:
      return { x: 1.0, y: 1.0, z: 1.0 }; // Default 1m cube
  }
};

const UVFloorPlanCanvas = ({
  data: structureData,
  selectedRoom = null,
  // The floor the host has picked, null for all floors. `data` arrives
  // already scoped to it; the renderer needs the scope itself to know it is
  // not stacking floors (see `ghostStoriesFor`).
  selectedStory = null,
  // External controls
  externalShowRoomLabels,
  showFixtures = true,
  showElectrical = true,
  showFurnishings = true,
  showStaging = true,
  showMeasurements = true,
  measurementFontPx,
  showDemoedWalls = true,
  onScaleChange,
  onPanChange,
  // `({ width, height, pan, scale, rotation })` whenever the view moves —
  // auto-fit included, which `onPanChange` (gestures only) never reports.
  // For a host drawing its own layer over the canvas: feed it to
  // `worldToViewScreen` / `viewScreenToWorld` (utils/geometryUtils).
  onViewChange,
  onShowRoomLabelsChange,
  onFixturesToggle,
  onFurnishingsToggle,
  onItemSelect,
  onWallLengthDrag,
  onWallLengthDragStart,
  onWallLengthDragEnd,
  selectedItem: externalSelectedItem = null, // Accept selectedItem from parent
  objectInteractionMode = 'move', // 'select', 'move', 'resize' — defaults to 'move' for backward compat
  // 'bay' | 'run' — which scope the open cabinet panel is editing, and so what
  // the highlight paints for a run-owned box. See `hooks/useCabinetEditScope`.
  cabinetEditScope = 'bay',
  debugMode = false,
  debugPanelCollapsed = false,
  disabledConstraints = [],
  structureWalls = [], // Structure-level walls for drawing associated walls
  debugMarkers = [], // Debug markers to render
  measurementMode = 'wall',
  dataSource = 'bold', // Data source mode
  placementMode = null, // Placement mode for adding new objects
  // Part Wall's two clicks: the structure document's openings (their edges
  // are where an end snaps) and `setStart`, which records the first click on
  // `placementMode.partWallStart` (`utils/partWallDemo`).
  partWallDemo = null,
  roomDrawing = null, // Room drawing state from useRoomDrawing hook
  cabinetRunDrawing = null, // Cabinet-run drawing state from useCabinetRunDrawing hook
  recessedRowDrawing = null, // Recessed-row drawing state from useRecessedRowDrawing
  wallChainDrawing = null, // Wall-chain drawing state from useWallChainDrawing
  cabinetApplianceDrop = null, // Cabinet-run appliance-drop state from useCabinetApplianceDrop hook
  runDrag = null, // Live run-end / bay drag from useRunDrag
  onUnpinCabinet = null, // (cabinet) => void — a click on a fixed cabinet's pin releases it
  // Filled every render with an agent's pointer: mouse events on this canvas
  // at a plan point (`2d/floorplanAddTrayActions`).
  pointerRef = null,
  vertexDelete = null, // Vertex delete state from useVertexDelete hook
  vertexAdd = null, // Vertex add state from useVertexAdd hook
  vertexMove = null, // Vertex move state from useVertexMove hook
  previewObject = null, // Preview object that follows cursor
  setPreviewObject = null, // Function to update preview object
  onPlaceObject = null, // Callback when object is placed
  previewRotation = 0, // Rotation for preview object
  onNotification = null, // Notification callback
  highlightedElements = { type: null, elements: [] }, // Elements to highlight
  initialScale = null, // Optional initial scale override
  // Optional region to auto-fit INSTEAD of the room / structure bounds:
  // `{ minX, maxX, minZ, maxZ }` in metres. The kitchen layout previews frame
  // the kitchen SECTION of an open-plan room this way, so a kitchen cell is
  // not a speck in the corner of a great-room thumbnail. Read only on the
  // auto-fit; the user can still pan/zoom away when not `readOnly`.
  fitBounds = null,
  electricalMode = false, // Electrical mode to show switches and outlets
  showDimensionsOnly = false, // Admin layer: stated dimensions, plan lightened
  showWiring = false, // Draw the switch → fixture wires
  connectionMode = false, // Connection mode for linking switches to lights
  connectionStart = null, // First object selected in connection mode
  onConnectionSelect = null, // Callback when object is selected in connection mode
  onWiringRunSelect = null, // Callback when a wiring run is clicked (null = cleared)
  wireShapeDrafts = null, // connectionId → shape, while a handle is dragged
  onWireShapeDrag = null, // (connectionId, shape|null) — live preview
  onWireShapeCommit = null, // (connectionId, shape) — the drop
  selectedWiringRunId = null, // The picked run, highlighted and deletable
  calloutDrawing = null, // Note placement state from useCalloutDrawing
  showCallouts = false, // Draw the callout (note) layer — admin-gated for now
  inspectedElectrical = null, // Devices the inspection found, or null when that layer is off
  onCalloutSelect = null, // Callback when a note is clicked (null = cleared)
  selectedCalloutId = null, // The picked note, highlighted and deletable
  onMeasurementSelect = null, // Callback when a measurement is selected
  previewStructure = null, // Optional structure clone with stretch plan applied; substituted upstream as `data` so the post-stretch state renders directly
  prePreviewStructure = null, // Pre-stretch structure (when in preview): used to find which walls changed so the orange overlay can highlight them on top of the new positions
  previewMap = null, // Map<measurementKey, newDistanceMeters> — affected labels render orange with old → new
  lockedKeys = null, // Set<measurementKey> — labels for these get a 🔒 prefix
  collisionTargets = null, // { walls: Set<id>, fixtures: Set<id> } — render in red when expansion would collide
  overlapWarningTargets = null, // { fixtures: Set<id> } — render in yellow when post-shrink translation overlaps another wall (non-blocking)
  // Stretch-boundary slider (only when previewing). The line splits the
  // structure into static (left/below) and moving (right/above) regions.
  // The user can drag it within `boundaryRange` to fine-tune the cut.
  stretchBoundary = null, // number — projection of the boundary along boundaryAxis.dir
  boundaryAxis = null, // { dir, anchor, mover } — direction and edited-wall endpoints
  boundaryRange = null, // { min, max } — valid clamp range for stretchBoundary
  // The Δ-dependent zone the boundary just snapped out of. When set, we
  // render a pill label adjacent to the purple line explaining why.
  // Shape: { wallId, currentLength, reason }.
  stretchBoundaryClampReason = null,
  onSetStretchBoundary = null, // (newBoundary: number) → void
  // Translate-rectangle: { near, far, min, max } in dir/perp projections (see
  // useStretchPlan.stretchRegion). Rendered as a draggable box; everything
  // inside translates, outside stays. `near` mirrors stretchBoundary.
  stretchRegion = null,
  onSetStretchFar = null, // (far: number) → void
  onSetStretchExtentEdge = null, // (side: 'min'|'max', value: number) → void
  readOnly = false, // When true, disables all interaction (pan, zoom, drag, selection)
  showCollisionIndicator = true, // When false, suppresses red outlines + hatching on colliding fixtures (admin-only diagnostic in some views)
  takeOffOverlay = null, // { field, room } - highlights room-info-panel "Show" selection
  enableTestOverlays = false,
  // `(view) => node`: a host layer drawn over the plan, inside the canvas's
  // own box, re-rendered with every pan / zoom. `view` is
  // `{ width, height, pan, scale, rotation }` — map with `worldToViewScreen` /
  // `viewScreenToWorld`. The layer owns its pointer events; leave the rest
  // `pointer-events: none` so the plan underneath still pans and selects.
  renderOverlay = null,
}) => {
  // Derive showRoomWallsBold from dataSource - bold mode shows room walls in bright red
  const showRoomWallsBold = dataSource === 'bold';

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Structure-level door lookup, used to hydrate slim room-copy doors
  // when running door-swing collision checks. Without this, callers
  // pass slim copies into checkPlacement, doorType reads as undefined,
  // and the door-swing loop silently no-ops — users can drag fixtures
  // straight through any door.
  const structureDoorsById = useMemo(
    () =>
      buildOpeningLookup({
        doors: structureData?.structureDoors || structureData?.doors,
      }),
    [structureData?.structureDoors, structureData?.doors]
  );
  const DEFAULT_SCALE = 70; // Fallback pixels per meter
  const [scale, setScale] = useState(initialScale || DEFAULT_SCALE);
  const scaleRef = useRef(initialScale || DEFAULT_SCALE);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ x: 0, y: 0 }); // Track current pan value
  // Live run preview ({ objects, runId, hideIds }): the run re-solved with a
  // pending appliance drop, a dragged run end or a dragged bay, drawn
  // ghosted. Cleared when the gesture ends. The
  // re-solve is coalesced to one per animation frame (mousemove fires far
  // faster) using the latest cursor position, so dragging stays smooth.
  const [appliancePreview, setAppliancePreview] = useState(null);
  // A COMMITTED preview (a finished bay/run drag) stays on screen until the
  // room store's mirror into `structureData.rooms` lands — the canvas draws
  // structureData, and dropping the ghost a render earlier flashed the
  // pre-drag pose. This effect runs after the mirror's render, so the ghost
  // hands over exactly when the real objects are in place.
  useEffect(() => {
    setAppliancePreview((p) => (p?.committed ? null : p));
  }, [structureData]);
  const appliancePreviewRaf = useRef(null);
  const appliancePreviewWorld = useRef(null);
  useEffect(() => {
    if (!cabinetApplianceDrop?.isActive) {
      setAppliancePreview(null);
      if (appliancePreviewRaf.current != null) {
        cancelAnimationFrame(appliancePreviewRaf.current);
        appliancePreviewRaf.current = null;
      }
    }
  }, [cabinetApplianceDrop?.isActive]);
  const [forceRedrawCounter, setForceRedrawCounter] = useState(0); // Force redraw when data refreshes
  // Selected-room highlight fade timer. State is declared up here; the
  // effects that drive it live below where `selectedItem` itself is
  // resolved (line ~370), to avoid TDZ on `selectedItem`.
  const [roomHighlightStartedAt, setRoomHighlightStartedAt] = useState(null);
  const [roomHighlightTick, setRoomHighlightTick] = useState(0);
  const lastRoomHighlightIdRef = useRef(null);

  // Listen for refreshData events to force redraw
  useEffect(() => {
    const handleRefreshData = () => {
      // Add a small delay to allow data to be fetched from Firebase first
      // The StructureViewerWrapper fetches data asynchronously after receiving this event
      setTimeout(() => {
        setForceRedrawCounter((prev) => prev + 1);
      }, 500);
    };

    window.addEventListener('refreshData', handleRefreshData);
    return () => window.removeEventListener('refreshData', handleRefreshData);
  }, []);
  const [hasInitializedView, setHasInitializedView] = useState(false);
  const autoFitCanvasSizeRef = useRef(null); // Canvas size used for last auto-fit
  // Track which selectedRoom we last auto-fit to. When the user drills
  // into a room (or switches between rooms), we re-fit the viewport to
  // that room's bounds. Without this, the auto-fit effect would only
  // re-run on canvas resize and the room would render at whole-
  // structure zoom — labels for the small room would barely be legible.
  const lastAutoFitRoomIdRef = useRef(null);
  const lastAppliedInitialScaleRef = useRef(null); // initialScale honored by the last auto-fit
  const initialPanRef = useRef({ x: 0, y: 0 }); // Store the initial centered pan
  const initialScaleRef = useRef(initialScale || DEFAULT_SCALE); // Store the initial scale for reset
  const [exportLoading, setExportLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false); // Use ref for immediate dragging state
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 }); // Use ref for immediate updates during drag

  // Reset dragging state when component mounts/unmounts or data changes
  useEffect(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setHasDraggedMouse(false);
    mouseMovementRef.current = 0;
    return () => {
      setIsDragging(false);
      isDraggingRef.current = false;
    };
  }, [structureData]); // Reset when structureData changes (e.g., coming back from another page)
  const [hasDraggedMouse, setHasDraggedMouse] = useState(false);
  // A programmatic tween (focusRoom / focusAll) owns the viewport while it
  // runs, so the auto-fit effect below stands aside instead of racing it.
  const tweenActiveRef = useRef(false);
  const tweenRafRef = useRef(null);
  // `tweenTo` is created in the effect that installs `window.floorPlanControls`,
  // which runs after the auto-fit effect below wants it. A ref bridges the two
  // without reordering either.
  const tweenToRef = useRef(null);
  // Latest room list for `focusRoom`'s satellite lookup. Same reason as
  // `tweenToRef`: the controls effect must not list `structureData` as a
  // dependency (re-installing `window.floorPlanControls` on every data
  // update would tear down an in-flight tween), so the rooms arrive by ref.
  const roomsRef = useRef(null);
  roomsRef.current = structureData?.rooms;
  const mouseMovementRef = useRef(0); // Track total mouse movement
  const [internalSelectedItem, setInternalSelectedItem] = useState(null);
  const [selectedFloorPlanMeasurement, setSelectedFloorPlanMeasurement] =
    useState(null);
  const measurementsRef = useRef([]);
  // What the electrical overlay last DREW: the endpoint screen positions and
  // the runs built from them. The wire hit test measures against these rather
  // than recomputing, so a click can never land on geometry that is not on
  // screen (the same contract measurementsRef has).
  const wiringHitRef = useRef({ screenPositions: new Map(), runs: [] });
  // What the callout overlay drew this frame, for the note hit test.
  const calloutHitRef = useRef([]);
  // Where the fixed-cabinet pins were last drawn (`fixedCabinetMarker`).
  const fixedPinHitRef = useRef([]);
  // Use external selected item if provided, otherwise use internal state
  const selectedItem =
    externalSelectedItem !== undefined
      ? externalSelectedItem
      : internalSelectedItem;

  // Run collision check whenever selection changes so invalid objects show red immediately
  const lastCheckedIdRef = useRef(null);
  useEffect(() => {
    if (selectedItem?.id && selectedItem.id !== lastCheckedIdRef.current) {
      lastCheckedIdRef.current = selectedItem.id;
      requestAnimationFrame(() => {
        runInitialCollisionCheck(selectedItem);
        setForceRedrawCounter((c) => c + 1);
      });
    } else if (!selectedItem) {
      lastCheckedIdRef.current = null;
    }
  }, [selectedItem?.id]);
  // Selected-room highlight fade timer (paired with the state declared
  // earlier; `selectedItem` is resolved above so referencing it here is
  // safe).
  const isRoomLabelSelected = !!(selectedItem && selectedItem.isRoomLabel);
  const selectedRoomLabelId = isRoomLabelSelected ? selectedItem?.id : null;
  useEffect(() => {
    if (
      selectedRoomLabelId &&
      selectedRoomLabelId !== lastRoomHighlightIdRef.current
    ) {
      lastRoomHighlightIdRef.current = selectedRoomLabelId;
      setRoomHighlightStartedAt(Date.now());
    } else if (!selectedRoomLabelId) {
      lastRoomHighlightIdRef.current = null;
      setRoomHighlightStartedAt(null);
    }
  }, [selectedRoomLabelId]);
  // While a fade is in progress, advance a tick every animation frame so
  // the draw effect re-runs and the alpha decreases smoothly. Stops on
  // its own once the fade completes.
  useEffect(() => {
    if (roomHighlightStartedAt == null) return undefined;
    let raf;
    const total = HIGHLIGHT_FADE_DELAY_MS + HIGHLIGHT_FADE_DURATION_MS;
    const loop = () => {
      const elapsed = Date.now() - roomHighlightStartedAt;
      setRoomHighlightTick((t) => t + 1);
      if (elapsed < total) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomHighlightStartedAt]);

  // Use external props directly - make component controlled
  const showRoomLabels = externalShowRoomLabels ?? false;
  // Determine what objects to show based on fixtures and furnishings toggles
  // Staging is a layer like the others: without it here, turning the other
  // three off would stop objects being drawn at all and the Staging switch
  // would do nothing.
  const showObjects =
    showFixtures || showElectrical || showFurnishings || showStaging;
  const shouldShowMeasurements = showMeasurements;

  // Notify parent of scale changes
  useEffect(() => {
    if (onScaleChange) onScaleChange(scale);
  }, [scale, onScaleChange]);

  // Keep refs in sync with state
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const [rotation, setRotation] = useState(0);
  // Debug hook, in the house style (cf. `window.__DEBUG_SCENE__`): the
  // plan's world → screen mapping, so a browser check can find a wall on
  // the canvas by its geometry instead of by a screenshot. Dev only.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    window.__DEBUG_FLOORPLAN__ = {
      worldToScreen: (x, z) =>
        worldToScreenCoordinates(x, z, canvasRef.current, pan, scale, rotation),
    };
    return () => {
      delete window.__DEBUG_FLOORPLAN__;
    };
  }, [pan, scale, rotation]);
  const [worldX, setWorldX] = useState(null);
  const [worldY, setWorldY] = useState(null);
  const [exteriorDoors, setExteriorDoors] = useState([]);

  // Mouse position for placement mode
  const [mouseWorldPos, setMouseWorldPos] = useState({ x: 0, z: 0 });

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [activeResizeHandle, setActiveResizeHandle] = useState(null);
  const [resizeStartPos, setResizeStartPos] = useState(null);
  const [originalObjectState, setOriginalObjectState] = useState(null);
  const [tempResizedObject, setTempResizedObject] = useState(null);

  // Object dragging state
  const [isDraggingObject, setIsDraggingObject] = useState(false);
  const [objectDragStart, setObjectDragStart] = useState(null);
  const [originalObjectPosition, setOriginalObjectPosition] = useState(null);
  // Ref to store last dragged object state (position + rotation) for use at drag-end
  const lastDraggedObjectRef = useRef(null);
  // Ref to store redirected drag target (e.g., parent bathtub when dragging tub-surface)
  const dragRedirectTargetRef = useRef(null);
  // Debug state for collision overlay (updated every drag frame, no re-render)
  const collisionDebugRef = useRef(null);
  // Last non-null collision debug snapshot, preserved across drag-end so the
  // user can copy the trace after observing a failed snap (Shift+D keybind).
  const lastCollisionDebugRef = useRef(null);

  // State for dragging wall-mounted elements (doors, windows, openings)
  const [isDraggingWallElement, setIsDraggingWallElement] = useState(false);
  // Touch Move affordances: grab handles where a finger is (or has been)
  // the pointer, and which pointer kind is driving the current drag.
  const grabHandlesEnabled = useCoarsePointer();
  const [dragPointer, setDragPointer] = useState(null);
  const [wallElementDragStart, setWallElementDragStart] = useState(null);
  const [originalWallElementPosition, setOriginalWallElementPosition] =
    useState(null);

  // Wall editing state
  const [isDraggingWall, setIsDraggingWall] = useState(false);
  // A run-end or bay drag in flight (useRunDrag owns the gesture's data).
  const [isDraggingRun, setIsDraggingRun] = useState(false);
  const [activeWallHandle, setActiveWallHandle] = useState(null); // 'start' or 'end'
  const [wallDragStart, setWallDragStart] = useState(null);
  const [originalWallState, setOriginalWallState] = useState(null);
  // Removed tempEditedWall - using direct updates instead
  const [wallRotationDebug, setWallRotationDebug] = useState(null);
  const [hallways, setHallways] = useState([]);

  // Helper function to check if an item is a wall
  // Thin wrapper over the pure helper (Phase 2a) — same signature as before,
  // so all call sites stay byte-identical.
  const isWall = (item) => itemIsWall(item, structureData, structureWalls);
  const [selectedPathRoom, setSelectedPathRoom] = useState(null);
  const [roomPolygons, setRoomPolygons] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [containerReady, setContainerReady] = useState(false);
  const [autoPanState, setAutoPanState] = useState({
    hasAutoPanned: false,
    originalPan: null,
  });

  useEffect(() => {
    if (!onViewChange || !hasInitializedView) return;
    onViewChange({
      width: canvasSize.width,
      height: canvasSize.height,
      pan,
      scale,
      rotation,
    });
  }, [onViewChange, hasInitializedView, canvasSize, pan, scale, rotation]);

  // Memoized canvas size update function
  const handleCanvasResize = useCallback(() => {
    const newSize = updateCanvasSize(containerRef.current, canvasRef.current);
    if (newSize) {
      setCanvasSize(newSize);
    }
  }, []);

  // Initialize view (auto-fit scale and center pan) when structure data and canvas size are available.
  // Re-runs when canvas size changes significantly (e.g. container finished layout or window resized)
  // so the floor plan always fills the screen.
  useEffect(() => {
    if (!structureData || !canvasSize.width || !canvasSize.height) return;

    const currentRoomId = selectedRoom?.id ?? null;
    const roomChanged = currentRoomId !== lastAutoFitRoomIdRef.current;
    // An explicit scale request from the parent (render pages, PDF
    // capture) must always be honored — callers report THIS value as the
    // drawn scale, so silently keeping an older fit would make every
    // downstream scale ruler wrong.
    const initialScaleChanged =
      initialScale !== lastAppliedInitialScaleRef.current;

    // Skip if neither canvas size nor selected-room id changed
    // meaningfully since last auto-fit. (User-initiated pan / zoom
    // happens through different paths; this guard keeps the effect
    // from snapping the viewport back after they've adjusted it.)
    const prev = autoFitCanvasSizeRef.current;
    if (prev && !roomChanged && !initialScaleChanged) {
      const dw = Math.abs(canvasSize.width - prev.width);
      const dh = Math.abs(canvasSize.height - prev.height);
      if (dw < 20 && dh < 20) return;
    }

    // Stand aside for a programmatic tween. `focusAll` / `focusRoom` animate
    // scale and pan over ~800ms, but they are called alongside a selection
    // change — so this effect re-ran on the very next render (roomChanged)
    // and slammed the same destination in with `setScale`/`setPan`,
    // overwriting the animation before a frame of it could be seen. Exiting
    // to the whole structure from the breadcrumb looked like a hard cut.
    //
    // The bookkeeping still has to be recorded: the tween IS the fit for this
    // room, so leaving the refs stale would let a later render re-enter here
    // and snap the viewport after the animation had already landed.
    if (tweenActiveRef.current) {
      lastAutoFitRoomIdRef.current = currentRoomId;
      lastAppliedInitialScaleRef.current = initialScale;
      setHasInitializedView(true);
      // `autoFitCanvasSizeRef` is deliberately NOT touched: a window resize
      // during those 800ms still deserves a real fit once the tween is done,
      // and the size guard above already no-ops when nothing actually moved.
      return;
    }

    // Calculate bounds. In single-room view (selectedRoom set), fit
    // to that room only so the user gets a useful zoom level for the
    // room they drilled into — fitting the whole structure here
    // makes small rooms render with unreadable labels.
    let minX = Infinity,
      maxX = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;

    const accumulateWall = (wall) => {
      if (!wall?.center) return;
      const x = wall.center.x || 0;
      const z = wall.center.z || 0;
      const halfLength = (wall.dimensions?.x || 1) / 2;
      const dir = apply3DRotationTo2D(wall.rotation);
      const dx = dir[0] * halfLength;
      const dz = dir[1] * halfLength;
      minX = Math.min(minX, x - dx, x + dx);
      maxX = Math.max(maxX, x - dx, x + dx);
      minZ = Math.min(minZ, z - dz, z + dz);
      maxZ = Math.max(maxZ, z - dz, z + dz);
    };
    const accumulatePolygon = (corners) => {
      corners?.forEach((corner) => {
        const x = corner.x || 0;
        const z = corner.z || 0;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minZ = Math.min(minZ, z);
        maxZ = Math.max(maxZ, z);
      });
    };

    if (fitBounds) {
      ({ minX, maxX, minZ, maxZ } = fitBounds);
    } else if (selectedRoom) {
      // Same group scope as `focusRoom` below — the auto-fit and the
      // explicit focus must frame the same thing.
      for (const member of roomGroupMembers(
        selectedRoom,
        structureData?.rooms
      )) {
        member.walls?.forEach(accumulateWall);
        accumulatePolygon(member.floors?.[0]?.polygonCorners);
      }
    } else {
      structureData.walls?.forEach(accumulateWall);
      structureData.rooms?.forEach((room) =>
        accumulatePolygon(room.floors?.[0]?.polygonCorners)
      );
    }

    // Check if we have valid bounds
    if (isFinite(minX) && isFinite(maxX) && isFinite(minZ) && isFinite(maxZ)) {
      const centerX = (minX + maxX) / 2;
      const centerZ = (minZ + maxZ) / 2;

      let autoFitScale;
      if (initialScale) {
        autoFitScale = initialScale;
      } else {
        const fit = computeMeasurementAwareFitScale(
          minX,
          maxX,
          minZ,
          maxZ,
          canvasSize.width,
          canvasSize.height,
          showMeasurements
        );
        // Floor at 10 px/m (anything tighter is unreadable).
        autoFitScale = fit !== null ? Math.max(10, fit) : DEFAULT_SCALE;
      }

      // Calculate initial pan to center the structure with the new scale
      const initialPan = {
        x: -centerX * autoFitScale,
        y: -centerZ * autoFitScale,
      };

      // Entering or leaving a room ANIMATES; first paint and resizes snap.
      //
      // This effect is the only thing that moves the viewport when the room
      // changes from outside this component — the breadcrumb's "exit to
      // structure" lives on the wrapper and reaches the 3D camera only, so
      // in the floor plan the view simply re-fitted on the next render and
      // read as a hard cut. Animating here fixes every such caller at once,
      // and lands on exactly the fit this effect already computed (`focusAll`
      // recomputes its own bounds and disagrees — 10 vs 42 px/m on a stacked
      // scan — so tweening to ITS answer would zoom out far too far).
      //
      // First paint must not animate (there is nothing to animate from), and
      // a resize should track the container immediately.
      const shouldAnimate = roomChanged && hasInitializedView;
      initialScaleRef.current = autoFitScale;
      initialPanRef.current = initialPan;
      if (shouldAnimate) {
        tweenToRef.current?.(autoFitScale, initialPan);
      } else {
        setScale(autoFitScale);
        setPan(initialPan);
        panRef.current = initialPan;
      }
      autoFitCanvasSizeRef.current = { ...canvasSize };
      lastAutoFitRoomIdRef.current = currentRoomId;
      lastAppliedInitialScaleRef.current = initialScale;
      setHasInitializedView(true);
    }
  }, [
    structureData,
    canvasSize,
    initialScale,
    selectedRoom,
    showMeasurements,
    fitBounds,
  ]);

  // Set up resize observer and handle container readiness
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    const setupResize = () => {
      requestAnimationFrame(() => {
        if (!containerRef.current || !canvasRef.current) {
          setTimeout(setupResize, 10);
          return;
        }

        setContainerReady(true);

        // Set up resize observer
        const cleanup = setupResizeObserver(
          containerRef.current,
          handleCanvasResize
        );

        // Force initial size update with a slight delay
        setTimeout(() => {
          handleCanvasResize();
        }, 0);

        return cleanup;
      });
    };

    const cleanup = setupResize();
    return cleanup;
  }, [handleCanvasResize]);

  // Also handle window resize as fallback
  useEffect(() => {
    const handleResize = () => {
      setTimeout(handleCanvasResize, 100); // Small delay to ensure layout is complete
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleCanvasResize]);

  // Use structureData directly from props - no local copy
  useEffect(() => {
    if (!structureData) {
      setError('No structure data provided');
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [structureData]);

  // Shift+D: copy the most recent collision-debug snapshot to the clipboard
  // so the user can paste it into a bug report. Survives drag-end via
  // lastCollisionDebugRef.
  useEffect(() => {
    const handleCopyCollisionDebug = (e) => {
      if (!(e.shiftKey && (e.key === 'D' || e.key === 'd'))) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable)
        return;
      const debug = lastCollisionDebugRef.current;
      if (!debug) {
        console.log('[debug-copy] no collision debug snapshot available');
        return;
      }
      const json = JSON.stringify(
        debug,
        (key, value) => {
          // Drop noisy / non-serializable fields.
          if (key === '_snappedWall' && value && typeof value === 'object') {
            return { id: value.id, type: value.type, rotation: value.rotation };
          }
          return value;
        },
        2
      );
      navigator.clipboard.writeText(json).then(
        () => console.log('[debug-copy] collision debug copied to clipboard'),
        (err) => console.error('[debug-copy] failed:', err)
      );
    };
    window.addEventListener('keydown', handleCopyCollisionDebug);
    return () =>
      window.removeEventListener('keydown', handleCopyCollisionDebug);
  }, []);

  useEffect(() => {
    if (!structureData) return;

    // Find all exterior doors in the floor plan
    const extDoors = findExteriorDoors(structureData, rotation);
    setExteriorDoors(extDoors);
  }, [structureData]);

  useEffect(() => {
    if (!structureData || !exteriorDoors || exteriorDoors.length === 0) return;

    // Store exteriorDoors in structureData for reference
    structureData.exteriorDoors = exteriorDoors;

    // Find hallways
    const foundHallways = findHallways(structureData, exteriorDoors, rotation);
    setHallways(foundHallways);
  }, [structureData, exteriorDoors]);

  // For new data format, we don't need to calculate rotation from walls
  // The objects already have absolute positions

  // Initialize wall rotation debug when a wall is selected
  useEffect(() => {
    if (selectedItem && isWall(selectedItem) && !isDraggingWall) {
      // Initialize debug info for selected wall
      const wallRotation = selectedItem.rotation?.y || 0;
      setWallRotationDebug({
        original: wallRotation,
        current: wallRotation,
        delta: 0,
      });
    } else if (!selectedItem || !isWall(selectedItem)) {
      // Clear debug when wall is deselected
      setWallRotationDebug(null);
    }
  }, [selectedItem, isDraggingWall]);

  // Clear temp objects when parent has updated or object is deselected
  useEffect(() => {
    if (!selectedItem) {
      // Don't clear if we're in the middle of dragging
      if (
        isDraggingWall ||
        isResizing ||
        isDraggingObject ||
        isDraggingWallElement
      ) {
        return;
      }
      setTempResizedObject(null);
      return;
    }

    // Check if parent has updated with our temp values
    if (tempResizedObject && selectedItem.dimensions) {
      const dimMatch =
        Math.abs(tempResizedObject.dimensions.x - selectedItem.dimensions.x) <
          0.001 &&
        Math.abs(tempResizedObject.dimensions.y - selectedItem.dimensions.y) <
          0.001 &&
        Math.abs(tempResizedObject.dimensions.z - selectedItem.dimensions.z) <
          0.001;

      const centerMatch =
        Math.abs(tempResizedObject.center.x - selectedItem.center.x) < 0.001 &&
        Math.abs(tempResizedObject.center.z - selectedItem.center.z) < 0.001;

      if (dimMatch && centerMatch && !selectedItem.isResized) {
        // Delay clearing to ensure the updated selectedItem is rendered first
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTempResizedObject(null);
          });
        });
      }
    }

    // Cleanup for resized objects only
  }, [selectedItem, tempResizedObject]);

  // Effect to restore original pan when panel closes
  useEffect(() => {
    // If no item is selected and we have auto-panned, restore original position
    if (
      !selectedItem &&
      autoPanState.hasAutoPanned &&
      autoPanState.originalPan
    ) {
      // Animate back to original position
      const startPan = { ...pan };
      const targetPan = autoPanState.originalPan;
      const animationDuration = 300; // ms
      const startTime = performance.now();

      const animatePan = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);

        // Ease-out cubic
        const easeOut = 1 - Math.pow(1 - progress, 3);

        const newPanX = startPan.x + (targetPan.x - startPan.x) * easeOut;
        const newPanY = startPan.y + (targetPan.y - startPan.y) * easeOut;
        setPan({ x: newPanX, y: newPanY });

        if (progress < 1) {
          requestAnimationFrame(animatePan);
        } else {
          // Reset auto-pan state after animation completes
          setAutoPanState({
            hasAutoPanned: false,
            originalPan: null,
          });
        }
      };

      requestAnimationFrame(animatePan);
    }
  }, [selectedItem, autoPanState, pan]);

  // Get data to render based on selected room
  // Thin wrapper over the pure helper (Phase 2a) — same signature as before.
  const getDataToRender = (dataParam) =>
    filterDataToSelectedRoom(dataParam, selectedRoom);

  // Helper to get elements with room context preserved
  // getElementsWithRoomContext is now imported from ./2d/floorplanDataView
  // (Phase 2a — it was already pure).

  const testOverlayPositions = useMemo(() => {
    if (!enableTestOverlays || !structureData || !canvasRef.current) return [];
    if (!showObjects) return [];

    const dataToRender = getDataToRender(structureData);
    if (!dataToRender) return [];

    const hasRoomStructure =
      dataToRender.rooms &&
      dataToRender.rooms.length > 0 &&
      dataToRender.rooms.some(
        (room) => room.walls || room.objects || room.doors || room.windows
      );

    let objects = [];
    if (hasRoomStructure && dataToRender.rooms) {
      objects = getElementsWithRoomContext(dataToRender.rooms, 'objects');
    } else if (dataToRender.objects?.length > 0) {
      objects = dataToRender.objects;
    }

    objects = objects.filter((obj) =>
      shouldShowObjectInLayer(obj, {
        showElectrical,
        showFixtures,
        showFurnishings,
        showStaging,
        electricalMode,
      })
    );

    const typeCounts = {};
    return objects
      .filter((obj) => obj.center && obj.dimensions)
      .map((obj) => {
        const type = getObjectType(obj).toLowerCase();
        typeCounts[type] = typeCounts[type] || 0;
        const typeIndex = typeCounts[type];
        typeCounts[type]++;

        const screenPos = worldToScreenCoordinates(
          obj.center.x,
          obj.center.z,
          canvasRef.current,
          pan,
          scale,
          rotation
        );

        const dimX = Array.isArray(obj.dimensions)
          ? obj.dimensions[0]
          : obj.dimensions.x || 0;
        const dimZ = Array.isArray(obj.dimensions)
          ? obj.dimensions[2]
          : obj.dimensions.z || 0;

        const objAngle = obj.rotation?.y || 0;
        const cos = Math.abs(Math.cos(objAngle));
        const sin = Math.abs(Math.sin(objAngle));
        const aabbW = dimX * cos + dimZ * sin;
        const aabbH = dimX * sin + dimZ * cos;

        return {
          id: obj.id,
          type,
          typeIndex,
          screenX: screenPos.x,
          screenY: screenPos.y,
          screenWidth: Math.abs(aabbW * scale),
          screenHeight: Math.abs(aabbH * scale),
        };
      });
  }, [
    enableTestOverlays,
    structureData,
    selectedRoom,
    pan,
    scale,
    rotation,
    canvasSize,
    showObjects,
    showFixtures,
    showElectrical,
    showFurnishings,
    showStaging,
    electricalMode,
    showDimensionsOnly,
    dataSource,
    forceRedrawCounter,
  ]);

  const testCollidingIds = useMemo(() => {
    if (!enableTestOverlays || !structureData) return new Set();
    const dataToRender = getDataToRender(structureData);
    if (!dataToRender?.rooms) return new Set();
    const room =
      selectedRoom ||
      (dataToRender.rooms.length === 1 ? dataToRender.rooms[0] : null);
    if (!room) return new Set();
    return collidingObjectIds(
      {
        walls: room.walls || [],
        doors: room.doors || [],
        windows: room.windows || [],
        openings: room.openings || [],
        objects: room.objects || [],
        floors: room.floors || [],
        disabledConstraints: room.disabledConstraints || [],
      },
      structureData
    );
  }, [enableTestOverlays, structureData, selectedRoom]);

  // Handler for PDF export. Answers the export's own result — `{ success,
  // filename }` or `{ success: false, error }` — which the menu ignores and
  // an agent's export_floor_plan_pdf reads.
  const handleExportPDF = useCallback(async () => {
    setExportLoading(true);
    try {
      // Determine project name and view type
      const projectName = structureData?.name || 'Structure';
      const viewType = selectedRoom
        ? `Room ${selectedRoom.id}`
        : 'Full Floorplan';

      // Get projectId from structureData prop
      const projectId = structureData?.projectId;

      const result = await exportFloorplanToPDF(
        canvasRef,
        projectName,
        viewType,
        projectId,
        true, // Save to project
        scale // pixelsPerMeter for exact architectural scaling
      );

      if (!result.success) {
        console.error('Failed to export PDF:', result.error);
        if (onNotification) {
          onNotification({
            message: 'Failed to export PDF. Please try again.',
            type: 'error',
          });
        } else {
          alert('Failed to export PDF. Please try again.');
        }
      }
      return result;
    } catch (error) {
      console.error('Export error:', error);
      if (onNotification) {
        onNotification({
          message: 'An error occurred while exporting. Please try again.',
          type: 'error',
        });
      } else {
        alert('An error occurred while exporting. Please try again.');
      }
      return { success: false, error: error.message };
    } finally {
      setExportLoading(false);
    }
  }, [canvasRef, structureData, selectedRoom]);

  // Expose handlers for external controls
  useEffect(() => {
    const zoomTowardScreenCenter = (newScale) => {
      const prevScale = scaleRef.current;
      const prevPan = panRef.current;
      if (newScale === prevScale) return;
      const scaleChange = newScale / prevScale;
      const newPan = {
        x: prevPan.x * scaleChange,
        y: prevPan.y * scaleChange,
      };
      scaleRef.current = newScale;
      panRef.current = newPan;
      setScale(newScale);
      setPan(newPan);
    };

    const tweenTo = (targetScale, targetPan, duration = 800) => {
      // Supersede a tween already in flight rather than interleaving two
      // rAF loops writing the same state.
      if (tweenRafRef.current !== null) {
        cancelAnimationFrame(tweenRafRef.current);
        tweenRafRef.current = null;
      }
      tweenActiveRef.current = true;
      const startScale = scaleRef.current;
      const startPan = { ...panRef.current };
      const startTime = performance.now();
      // Smoothstep-ish ease-in-out so movement starts slow, accelerates,
      // then settles — easier for the user to follow visually.
      const ease = (x) =>
        x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
      const tick = (now) => {
        const elapsed = Math.min(1, (now - startTime) / duration);
        const t = ease(elapsed);
        const s = startScale + (targetScale - startScale) * t;
        const px = startPan.x + (targetPan.x - startPan.x) * t;
        const py = startPan.y + (targetPan.y - startPan.y) * t;
        scaleRef.current = s;
        panRef.current = { x: px, y: py };
        setScale(s);
        setPan({ x: px, y: py });
        if (elapsed < 1) {
          tweenRafRef.current = requestAnimationFrame(tick);
        } else {
          tweenRafRef.current = null;
          tweenActiveRef.current = false;
        }
      };
      tweenRafRef.current = requestAnimationFrame(tick);
    };
    tweenToRef.current = tweenTo;

    const focusRoom = (room) => {
      if (!room || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const viewW = canvas.width / dpr;
      const viewH = canvas.height / dpr;

      let minX = Infinity,
        maxX = -Infinity,
        minZ = Infinity,
        maxZ = -Infinity;
      // Fit the whole room GROUP: a satellite (a closet / pantry / nook the
      // pipeline carved off this room — see `utils/roomGroupUtils`) is drawn
      // as part of the room, so framing on the host's polygon alone would
      // crop it off the edge of the view. One member for a room with no
      // satellites, and this is the room's own polygon exactly as before.
      // Rooms come from a ref, not from `structureData` directly: this
      // effect installs `window.floorPlanControls` and must not re-run (and
      // tear down an in-flight tween) every time the scan data updates.
      const group = roomGroupMembers(room, roomsRef.current);
      for (const member of group) {
        const corners = member.floors?.[0]?.polygonCorners || [];
        for (const corner of corners) {
          const x = Array.isArray(corner) ? corner[0] : (corner.x ?? 0);
          const z = Array.isArray(corner) ? corner[2] : (corner.z ?? 0);
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minZ = Math.min(minZ, z);
          maxZ = Math.max(maxZ, z);
        }
      }
      if (!isFinite(minX) || minX >= maxX || minZ >= maxZ) return;

      const fitScale = computeMeasurementAwareFitScale(
        minX,
        maxX,
        minZ,
        maxZ,
        viewW,
        viewH,
        showMeasurements
      );
      if (fitScale === null) return;
      const targetScale = Math.max(10, Math.min(1000, fitScale));
      const cx = (minX + maxX) / 2;
      const cz = (minZ + maxZ) / 2;
      const targetPan = { x: -cx * targetScale, y: -cz * targetScale };
      tweenTo(targetScale, targetPan);
    };

    // Animate back to a fresh auto-fit of the whole structure. We can't
    // reuse `initialScaleRef.current` because the per-room auto-fit
    // overwrites it whenever the user enters a single-room view — so
    // `focusAll` would tween back to the LAST room's fit, not the whole
    // structure. Recompute bounds + fit-scale on demand.
    const focusAll = () => {
      if (!canvasRef.current || !structureData) return;
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const viewW = canvas.width / dpr;
      const viewH = canvas.height / dpr;

      let minX = Infinity,
        maxX = -Infinity,
        minZ = Infinity,
        maxZ = -Infinity;

      structureData.walls?.forEach((wall) => {
        if (!wall?.center) return;
        const x = wall.center.x || 0;
        const z = wall.center.z || 0;
        const halfLength = (wall.dimensions?.x || 1) / 2;
        const dir = apply3DRotationTo2D(wall.rotation);
        const dx = dir[0] * halfLength;
        const dz = dir[1] * halfLength;
        minX = Math.min(minX, x - dx, x + dx);
        maxX = Math.max(maxX, x - dx, x + dx);
        minZ = Math.min(minZ, z - dz, z + dz);
        maxZ = Math.max(maxZ, z - dz, z + dz);
      });
      structureData.rooms?.forEach((room) => {
        room.floors?.[0]?.polygonCorners?.forEach((corner) => {
          const x = corner.x || 0;
          const z = corner.z || 0;
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minZ = Math.min(minZ, z);
          maxZ = Math.max(maxZ, z);
        });
      });

      if (!isFinite(minX) || minX >= maxX || minZ >= maxZ) {
        // No usable bounds — fall back to the cached initial.
        tweenTo(initialScaleRef.current, initialPanRef.current);
        return;
      }

      const fitScale = computeMeasurementAwareFitScale(
        minX,
        maxX,
        minZ,
        maxZ,
        viewW,
        viewH,
        showMeasurements
      );
      if (fitScale === null) {
        tweenTo(initialScaleRef.current, initialPanRef.current);
        return;
      }
      const targetScale = Math.max(10, Math.min(1000, fitScale));
      const cx = (minX + maxX) / 2;
      const cz = (minZ + maxZ) / 2;
      const targetPan = { x: -cx * targetScale, y: -cz * targetScale };
      tweenTo(targetScale, targetPan);
    };

    // Installed through the bridge: a preview mounted over the plan must not
    // take the plan's controls with it when it closes.
    return installFloorPlanControls(
      {
        zoomIn: () =>
          zoomTowardScreenCenter(Math.min(1000, scaleRef.current + 20)),
        zoomOut: () =>
          zoomTowardScreenCenter(Math.max(10, scaleRef.current - 20)),
        zoomReset: () => {
          setPan(initialPanRef.current);
          panRef.current = initialPanRef.current;
          setScale(initialScaleRef.current);
          scaleRef.current = initialScaleRef.current;
        },
        // Set zoom directly (used by the minimap-side scale slider). Pans
        // proportionally so the screen center stays fixed, matching the
        // zoomIn/Out behavior.
        setScale: (next) =>
          zoomTowardScreenCenter(Math.max(10, Math.min(1000, next))),
        focusRoom,
        focusAll,
        exportPDF: handleExportPDF,
        isExporting: () => exportLoading,
      },
      { readOnly }
    );
  }, [handleExportPDF, exportLoading, showMeasurements, readOnly]);

  // Drawing effect call lives further down, immediately after drawFloorPlan
  // is defined (the ctx passed to useFloorplanRenderer references it, so it
  // must be initialized first to avoid a temporal-dead-zone error).

  // Run initial collision check when drag starts so the object shows red immediately if invalid
  const runInitialCollisionCheck = (item) => {
    if (!item?.center || !item?.dimensions) return;
    const room =
      (item._roomId && structureData?.rooms
        ? structureData.rooms.find((r) => r.id === item._roomId)
        : null) || selectedRoom;
    if (!room) return;
    const roomData = {
      walls: room.walls || [],
      doors: room.doors || [],
      windows: room.windows || [],
      openings: room.openings || [],
      objects: room.objects || [],
      floors: room.floors || [],
    };
    const roomConstraints = room.disabledConstraints || [];
    const validation = checkPlacement(item, roomData, {
      excludeIds: [item.id],
      excludeGroupIds: item.fixtureGroupId ? [item.fixtureGroupId] : [],
      snappedWall: item._snappedWall || undefined,
      structureDoorsById,
    });
    const fixtureType = (item.type || '').toLowerCase();
    const filtered = filterCollisions(
      validation.collisions || [],
      fixtureType,
      roomConstraints
    );
    if (filtered.length > 0) {
      item.hasCollision = true;
    }
  };

  // Track what was selected at mouseDown time to prevent immediate dragging.
  // Declared in the coordinator (not the gestures hook) because drawFloorPlan
  // reads selectedAtMouseDownRef for the displaySelectedItem fallback.
  const selectedAtMouseDownRef = useRef(null);

  // Gesture handlers — extracted verbatim into ./2d/useFloorplanGestures.js.
  // The hook owns the byte-identical mouse / wheel / touch / click handlers
  // and their effects; the coordinator assembles ctx and calls it here so
  // hook/effect order is preserved. Returned handlers are bound in the JSX.
  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleClick,
    containerWheelRef,
  } = useFloorplanGestures({
    DEFAULT_SCALE,
    activeResizeHandle,
    activeWallHandle,
    autoPanState,
    boundaryAxis,
    canvasRef,
    canvasResizeHandlesFor,
    collisionDebugRef,
    connectionMode,
    containerRef,
    dataSource,
    dragRedirectTargetRef,
    dragStartRef,
    getDataToRender,
    getDefaultSize,
    getElementsWithRoomContext,
    hasDraggedMouse,
    isDraggingObject,
    isDraggingRef,
    isDraggingWall,
    isDraggingWallElement,
    isResizing,
    isWall,
    lastCollisionDebugRef,
    lastDraggedObjectRef,
    measurementsRef,
    wiringHitRef,
    calloutHitRef,
    fixedPinHitRef,
    onUnpinCabinet,
    mouseMovementRef,
    objectDragStart,
    objectInteractionMode,
    cabinetEditScope,
    grabHandlesEnabled,
    setDragPointer,
    onConnectionSelect,
    onWiringRunSelect,
    onWireShapeDrag,
    onWireShapeCommit,
    selectedWiringRunId,
    calloutDrawing,
    onCalloutSelect,
    selectedCalloutId,
    onItemSelect,
    onWallLengthDrag,
    onWallLengthDragStart,
    onWallLengthDragEnd,
    onMeasurementSelect,
    onNotification,
    onPanChange,
    onPlaceObject,
    onSetStretchBoundary,
    onSetStretchFar,
    onSetStretchExtentEdge,
    stretchRegion,
    originalObjectPosition,
    originalObjectState,
    originalWallElementPosition,
    originalWallState,
    pan,
    panRef,
    placementMode,
    partWallDemo,
    previewObject,
    previewRotation,
    readOnly,
    resizeStartPos,
    roomDrawing,
    cabinetRunDrawing,
    recessedRowDrawing,
    wallChainDrawing,
    cabinetApplianceDrop,
    runDrag,
    isDraggingRun,
    setIsDraggingRun,
    setAppliancePreview,
    appliancePreviewRaf,
    appliancePreviewWorld,
    rotation,
    scale,
    scaleRef,
    selectedAtMouseDownRef,
    selectedItem,
    selectedRoom,
    setActiveResizeHandle,
    setActiveWallHandle,
    setAutoPanState,
    setDragStart,
    setHasDraggedMouse,
    setInternalSelectedItem,
    setIsDragging,
    setIsDraggingObject,
    setIsDraggingWall,
    setIsDraggingWallElement,
    setIsResizing,
    setMouseWorldPos,
    setObjectDragStart,
    setOriginalObjectPosition,
    setOriginalObjectState,
    setOriginalWallElementPosition,
    setOriginalWallState,
    setPan,
    setPreviewObject,
    setResizeStartPos,
    setScale,
    setSelectedFloorPlanMeasurement,
    setTempResizedObject,
    setWallDragStart,
    setWallElementDragStart,
    setWallRotationDebug,
    setWorldX,
    setWorldY,
    showRoomLabels,
    structureData,
    structureDoorsById,
    structureWalls,
    tempResizedObject,
    vertexAdd,
    vertexDelete,
    vertexMove,
    wallDragStart,
    wallElementDragStart,
  });

  // An agent's pointer (`2d/floorplanAddTrayActions`): real mouse events on
  // this canvas at the screen point a plan point is drawn at, which React
  // hands to the same handlers the user's pointer reaches — so what an agent
  // places is what a click there places, and the click is not changed for
  // it. Filled every render: the mapping reads the current pan, zoom and
  // rotation, the exact inverse of the one the handlers use.
  if (pointerRef) {
    const screenAt = (x, z) =>
      worldToScreenCoordinates(x, z, canvasRef.current, pan, scale, rotation);
    const fire = (type, x, z) => {
      const canvas = canvasRef.current;
      if (!canvas) return false;
      const rect = canvas.getBoundingClientRect();
      const at = screenAt(x, z);
      canvas.dispatchEvent(
        new MouseEvent(type, {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: rect.left + at.x,
          clientY: rect.top + at.y,
          button: 0,
          buttons: type === 'mousedown' ? 1 : 0,
          detail: type === 'click' ? 1 : 0,
        })
      );
      return true;
    };
    pointerRef.current = readOnly
      ? null
      : {
          hover: (x, z) => fire('mousemove', x, z),
          down: (x, z) => fire('mousedown', x, z),
          up: (x, z) => fire('mouseup', x, z),
          click: (x, z) => fire('click', x, z),
          // What takes the press or the click before a placement, tested the
          // way the handlers test them: a selected wiring run's grab handle
          // (the press), a fixed cabinet's pin (the click releases it), a
          // note, a wiring run.
          takenBy: (x, z) => {
            if (!canvasRef.current) return null;
            const at = screenAt(x, z);
            if (onWireShapeCommit && wiringHitRef.current?.wireHandles?.size) {
              for (const pt of wiringHitRef.current.wireHandles.values())
                if (
                  Math.hypot(pt.x - at.x, pt.y - at.y) <=
                  WIRE_HANDLE_RADIUS_PX + 4
                )
                  return "A wiring run's handle";
            }
            if (
              onUnpinCabinet &&
              fixedPinHitRef.current?.length &&
              fixedPinAt(fixedPinHitRef.current, { x, z })
            )
              return "A fixed cabinet's pin";
            if (
              onCalloutSelect &&
              calloutHitRef.current?.length &&
              calloutAtPoint(calloutHitRef.current, at)
            )
              return 'A note';
            if (onWiringRunSelect && wiringHitRef.current) {
              const {
                runs,
                screenPositions,
                visibleIds,
                scale: hitScale,
              } = wiringHitRef.current;
              if (
                runAtPoint({
                  runs,
                  screenPositions,
                  visibleIds,
                  scale: hitScale,
                  point: at,
                })
              )
                return 'A wiring run';
            }
            return null;
          },
        };
  }

  // The overlay's room's wall finishes, so tile stops its baseboard as it
  // stops the panel's figure and the estimate's. Carried ON the overlay: the
  // renderer redraws when the overlay changes, so the plan is drawn again
  // once the types have loaded (the lookup is a new function then).
  const takeOffRooms = useMemo(
    () => (takeOffOverlay?.room ? [takeOffOverlay.room] : null),
    [takeOffOverlay?.room]
  );
  const { materialTypeOf: takeOffMaterialTypeOf } =
    useRegionMaterialTypes(takeOffRooms);
  const takeOffOverlayWithTypes = useMemo(
    () =>
      takeOffOverlay
        ? { ...takeOffOverlay, materialTypeOf: takeOffMaterialTypeOf }
        : null,
    [takeOffOverlay, takeOffMaterialTypeOf]
  );

  // Render the room-info-panel "Show" overlay on the floor plan.
  // Field is one of: 'floor', 'tile', 'wallLength', 'baseboard',
  // 'wallArea', 'paint'.
  const drawTakeOffOverlay = (ctx, overlay, currentScale) => {
    const {
      field,
      room,
      wallIndex: focusWallIndex = null,
      materialTypeOf,
    } = overlay;
    if (!room) return;
    const takeOffs = computeRoomTakeOffs(
      room,
      {
        doors: structureData?.doors,
        windows: structureData?.windows,
        openings: structureData?.openings,
      },
      { materialTypeOf }
    );
    const px = (n) => n / currentScale; // pixel size in world units

    // ── Floor / Tile: fill polygon, plus blocker rects for tile ──
    if (field === 'floor' || field === 'tile') {
      const corners = takeOffs.floorPolygonCorners;
      if (!corners || corners.length < 3) return;
      ctx.save();
      ctx.beginPath();
      corners.forEach((c, i) => {
        if (i === 0) ctx.moveTo(c.x, c.z);
        else ctx.lineTo(c.x, c.z);
      });
      ctx.closePath();
      const isTile = field === 'tile';
      ctx.fillStyle = isTile ? 'rgba(34,197,94,0.25)' : 'rgba(59,130,246,0.25)';
      ctx.strokeStyle = isTile ? '#16a34a' : '#3b82f6';
      ctx.lineWidth = px(2);
      ctx.fill();
      ctx.stroke();

      if (isTile) {
        ctx.fillStyle = 'rgba(239,68,68,0.45)';
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = px(1.5);
        for (const b of takeOffs.tileBlockers) {
          ctx.beginPath();
          b.corners.forEach((c, i) => {
            if (i === 0) ctx.moveTo(c.x, c.z);
            else ctx.lineTo(c.x, c.z);
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        }
      }

      // Centroid label with the value.
      let cx = 0;
      let cz = 0;
      for (const c of corners) {
        cx += c.x;
        cz += c.z;
      }
      cx /= corners.length;
      cz /= corners.length;
      const valM2 = isTile ? takeOffs.tileAreaM2 : takeOffs.floorAreaM2;
      const label = `${isTile ? 'Tile' : 'Floor'}: ${(valM2 * 10.764).toFixed(1)} ft²`;
      ctx.font = `${px(14)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(label).width;
      const padX = px(6);
      const padY = px(4);
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.strokeStyle = isTile ? '#16a34a' : '#3b82f6';
      ctx.lineWidth = px(1);
      ctx.beginPath();
      ctx.rect(cx - tw / 2 - padX, cz - px(9), tw + 2 * padX, px(18) + padY);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#111827';
      ctx.fillText(label, cx, cz + px(2));
      ctx.restore();
      return;
    }

    // ── Wall fields: per-wall labels ──
    const isArea =
      field === 'wallArea' || field === 'paint' || field === 'wallTile';
    const fmt = (v) =>
      isArea
        ? `${(v * 10.764).toFixed(1)} ft²`
        : `${(v * 3.281).toFixed(1)} ft`;
    const accent =
      field === 'wallTile'
        ? '#0d9488'
        : field === 'baseboard' || field === 'paint'
          ? '#f97316'
          : '#3b82f6';

    // Compute room centroid (for label inset direction).
    const corners = takeOffs.floorPolygonCorners;
    let roomCx = 0;
    let roomCz = 0;
    if (corners && corners.length >= 3) {
      for (const c of corners) {
        roomCx += c.x;
        roomCz += c.z;
      }
      roomCx /= corners.length;
      roomCz /= corners.length;
    }

    ctx.save();
    for (const s of takeOffs.wallStats) {
      const wall = s.wall;
      if (!wall || !wall.center) continue;
      // Wall tile only exists where a region is assigned — skip the rest
      // so the overlay reads as "these walls are tiled".
      if (field === 'wallTile' && !(s.wallTile > 0)) continue;
      // Derive direction + midpoint from wall endpoints so diagonal walls
      // get the right axis. Using `cos(ry), sin(ry)` is wrong because
      // `rotation.y = atan2(-dz, dx)` flips the sign of the Z component.
      const { dirX, dirZ, midX, midZ } = getWallAxisXZ(wall);
      // Inward normal: the side whose probe point lands inside the floor
      // polygon. "Toward the centroid" is wrong for an alcove wall of an
      // L-shaped room (the centroid can sit on its outer side); fall back
      // to it only when the probe is inconclusive.
      let nx = -dirZ;
      let nz = dirX;
      const probe = (wall.dimensions?.z ?? 0) / 2 + 0.05;
      const plusInside =
        corners?.length >= 3 &&
        isPointInsidePolygon(
          { x: midX + nx * probe, z: midZ + nz * probe },
          corners
        );
      const minusInside =
        corners?.length >= 3 &&
        isPointInsidePolygon(
          { x: midX - nx * probe, z: midZ - nz * probe },
          corners
        );
      const flip =
        plusInside === minusInside
          ? nx * (roomCx - midX) + nz * (roomCz - midZ) < 0
          : minusInside;
      if (flip) {
        nx = -nx;
        nz = -nz;
      }
      // Hovering a "Wall N" row in the panel: that wall full strength,
      // the rest dimmed so it stands out.
      ctx.globalAlpha =
        focusWallIndex == null || s.index === focusWallIndex ? 1 : 0.3;

      const segToWorld = (a) => ({
        x: midX + dirX * a,
        z: midZ + dirZ * a,
      });

      const len = s.length;
      // Baseboard mode highlights only the segments that contribute to
      // the metric; everything else (openings, fixture footprints) is
      // visually omitted. Other wall metrics highlight the full wall.
      const segments =
        field === 'baseboard'
          ? s.baseboardSegments || []
          : [[-len / 2, len / 2]];
      ctx.strokeStyle = accent;
      // Baseboard segments are drawn fatter (8 px) since they're often
      // short runs broken up by openings / fixtures and need to read
      // clearly. Other wall fields stay at 4 px.
      ctx.lineWidth = field === 'baseboard' ? px(8) : px(4);
      ctx.lineCap = 'round';
      // Suppress drawing baseboard runs under 0.5" — at most a sliver
      // between two adjacent fixtures and only adds clutter to the
      // overlay (the totals still account for them).
      const MIN_DRAW_LEN_M = field === 'baseboard' ? 0.5 * 0.0254 : 0;
      for (const [a0, a1] of segments) {
        if (a1 - a0 < MIN_DRAW_LEN_M) continue;
        const p0 = segToWorld(a0);
        const p1 = segToWorld(a1);
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.z);
        ctx.lineTo(p1.x, p1.z);
        ctx.stroke();
      }
      ctx.lineCap = 'butt';

      // Label near wall midpoint, inset toward room interior.
      const inset = 0.25; // meters
      const lx = midX + nx * inset;
      const lz = midZ + nz * inset;
      const value =
        field === 'wallLength'
          ? s.length
          : field === 'baseboard'
            ? s.baseboard
            : field === 'wallArea'
              ? s.area
              : field === 'wallTile'
                ? s.wallTile
                : s.paint;
      const label = `${s.name}: ${fmt(value)}`;
      ctx.font = `${px(11)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(label).width;
      const padX = px(5);
      const padY = px(3);
      const boxW = tw + 2 * padX;
      const boxH = px(14) + padY;
      ctx.fillStyle = 'rgba(255,255,255,0.94)';
      ctx.strokeStyle = accent;
      ctx.lineWidth = px(1);
      ctx.beginPath();
      ctx.rect(lx - boxW / 2, lz - boxH / 2, boxW, boxH);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#111827';
      ctx.fillText(label, lx, lz);
    }
    ctx.restore();
  };

  // Drawing effect + drawFloorPlan now live in ./2d/useFloorplanRenderer.js.
  // The coordinator assembles ctx and calls the hook here so hook/effect
  // order is preserved; the renderer owns its own drawing.
  useFloorplanRenderer(
    buildFloorplanContracts({
      structureData,
      forceRedrawCounter,
      scale,
      pan,
      selectedItem,
      showRoomLabels,
      showObjects,
      shouldShowMeasurements,
      rotation,
      selectedRoom,
      selectedStory,
      canvasSize,
      debugMode,
      measurementMode,
      dataSource,
      previewStructure,
      prePreviewStructure,
      stretchBoundary,
      stretchRegion,
      boundaryAxis,
      stretchBoundaryClampReason,
      previewMap,
      lockedKeys,
      collisionTargets,
      overlapWarningTargets,
      showFixtures,
      showElectrical,
      showFurnishings,
      showStaging,
      showDemoedWalls,
      tempResizedObject,
      isResizing,
      activeResizeHandle,
      isDraggingObject,
      isDraggingWall,
      previewObject,
      electricalMode,
      showDimensionsOnly,
      wireShapeDrafts,
      showWiring,
      selectedWiringRunId,
      connectionMode,
      selectedFloorPlanMeasurement,
      connectionStart,
      showCollisionIndicator,
      takeOffOverlay: takeOffOverlayWithTypes,
      roomHighlightTick,
      canvasRef,
      measurementsRef,
      wiringHitRef,
      calloutHitRef,
      fixedPinHitRef,
      measurementFontPx,
      roomDrawing,
      cabinetRunDrawing,
      recessedRowDrawing,
      wallChainDrawing,
      showCallouts,
      selectedCalloutId,
      calloutDrawing,
      inspectedElectrical,
      appliancePreview,
      runDrag,
      isDraggingRun,
      vertexDelete,
      vertexAdd,
      vertexMove,
      structureWalls,
      showRoomWallsBold,
      objectInteractionMode,
      cabinetEditScope,
      isDraggingWallElement,
      grabHandlesEnabled,
      dragPointer,
      activeWallHandle,
      originalWallState,
      roomHighlightStartedAt,
      debugPanelCollapsed,
      disabledConstraints,
      highlightedElements,
      collisionDebugRef,
      dragRedirectTargetRef,
      selectedAtMouseDownRef,
      canvasResizeHandlesFor,
      drawTakeOffOverlay,
    })
  );

  if (loading) {
    return (
      <div className="floor-plan-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="floor-plan-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div
      className="floor-plan-viewer"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Wall Rotation Debug Display with Diagram */}
      {debugMode &&
        wallRotationDebug &&
        selectedItem &&
        isWall(selectedItem) && (
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
              right: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              padding: '12px',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '12px',
              zIndex: 1000,
              pointerEvents: 'none',
              display: 'flex',
              gap: '15px',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                Wall Rotation
              </div>
              <div
                style={{
                  borderTop: '1px solid #666',
                  paddingTop: '4px',
                  marginBottom: '4px',
                }}
              ></div>
              <div>
                Original:{' '}
                {((wallRotationDebug.original * 180) / Math.PI).toFixed(1)}°
              </div>
              <div>
                Current:{' '}
                {((wallRotationDebug.current * 180) / Math.PI).toFixed(1)}°
              </div>
              <div>
                Delta: {((wallRotationDebug.delta * 180) / Math.PI).toFixed(1)}°
              </div>
              <div
                style={{
                  borderTop: '1px solid #666',
                  paddingTop: '4px',
                  marginTop: '4px',
                }}
              >
                Handle: {activeWallHandle === 'start' ? 'A' : 'B'}
              </div>
            </div>

            {/* Rotation Diagram */}
            <svg
              width="100"
              height="100"
              viewBox="-50 -50 100 100"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
              }}
            >
              {/* Grid lines */}
              <line
                x1="-50"
                y1="0"
                x2="50"
                y2="0"
                stroke="#444"
                strokeWidth="0.5"
              />
              <line
                x1="0"
                y1="-50"
                x2="0"
                y2="50"
                stroke="#444"
                strokeWidth="0.5"
              />

              {/* Axis labels */}
              <text x="45" y="-5" fill="#888" fontSize="10" textAnchor="end">
                +X
              </text>
              <text x="5" y="-45" fill="#888" fontSize="10">
                +Z
              </text>

              {/* 0° reference line (along +X axis) */}
              <line
                x1="0"
                y1="0"
                x2="35"
                y2="0"
                stroke="#666"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
              <text x="38" y="3" fill="#666" fontSize="8">
                0°
              </text>

              {/* Original wall direction */}
              <line
                x1="0"
                y1="0"
                x2={35 * Math.cos(wallRotationDebug.original)}
                y2={-35 * Math.sin(wallRotationDebug.original)}
                stroke="#ff9999"
                strokeWidth="2"
              />
              <circle
                cx={35 * Math.cos(wallRotationDebug.original)}
                cy={-35 * Math.sin(wallRotationDebug.original)}
                r="3"
                fill="#ff9999"
              />

              {/* Current wall direction */}
              <line
                x1="0"
                y1="0"
                x2={35 * Math.cos(wallRotationDebug.current)}
                y2={-35 * Math.sin(wallRotationDebug.current)}
                stroke="#66ff66"
                strokeWidth="2"
              />
              <circle
                cx={35 * Math.cos(wallRotationDebug.current)}
                cy={-35 * Math.sin(wallRotationDebug.current)}
                r="3"
                fill="#66ff66"
              />

              {/* Rotation arc */}
              {wallRotationDebug.delta !== 0 && (
                <path
                  d={`M ${25 * Math.cos(wallRotationDebug.original)} ${-25 * Math.sin(wallRotationDebug.original)}
                    A 25 25 0 ${Math.abs(wallRotationDebug.delta) > Math.PI ? 1 : 0} ${wallRotationDebug.delta > 0 ? 0 : 1}
                    ${25 * Math.cos(wallRotationDebug.current)} ${-25 * Math.sin(wallRotationDebug.current)}`}
                  fill="none"
                  stroke="#ffff66"
                  strokeWidth="1.5"
                />
              )}

              {/* Center point */}
              <circle cx="0" cy="0" r="3" fill="white" />

              {/* Legend */}
              <text x="-45" y="45" fill="#ff9999" fontSize="8">
                Original
              </text>
              <text x="5" y="45" fill="#66ff66" fontSize="8">
                Current
              </text>
            </svg>
          </div>
        )}
      <div
        className="floor-plan-container"
        ref={(node) => {
          containerRef.current = node;
          if (!readOnly) containerWheelRef(node);
        }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          {...(!readOnly && {
            onMouseDown: handleMouseDown,
            onMouseMove: handleMouseMove,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseLeave,
            onClick: handleClick,
            onDoubleClick: (e) => {
              if (roomDrawing?.isActive) {
                e.preventDefault();
                roomDrawing.closePolygon();
              } else if (cabinetRunDrawing?.isActive) {
                e.preventDefault();
                cabinetRunDrawing.closeRun();
              }
            },
          })}
          className="floor-plan-canvas"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            touchAction: readOnly ? 'auto' : 'none',
            cursor: readOnly ? 'default' : 'auto',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            pointerEvents: readOnly ? 'none' : 'auto',
          }}
        />
        {enableTestOverlays &&
          testOverlayPositions.map((ov) => (
            <div
              key={ov.id}
              className="floor-plan-test-overlay"
              data-testid={`object-${ov.type}-${ov.typeIndex}`}
              data-object-id={ov.id}
              data-object-type={ov.type}
              data-colliding={testCollidingIds.has(ov.id) ? 'true' : undefined}
              style={{
                left: ov.screenX - ov.screenWidth / 2,
                top: ov.screenY - ov.screenHeight / 2,
                width: ov.screenWidth,
                height: ov.screenHeight,
              }}
            />
          ))}
        {/* Typing a note's text, parked where its cloud will be drawn. Lives
            here rather than in the host because this is where the camera is:
            the world → screen mapping is what puts the field on the spot the
            user clicked. */}
        {calloutDrawing?.pending && canvasRef.current && (
          <CalloutTextEditor
            key={`${calloutDrawing.pending.id || 'new'}-${calloutDrawing.pending.cloud.x}-${calloutDrawing.pending.cloud.z}`}
            {...(() => {
              const p = worldToScreenCoordinates(
                calloutDrawing.pending.cloud.x,
                calloutDrawing.pending.cloud.z,
                canvasRef.current,
                pan,
                scale,
                rotation
              );
              return { left: p.x, top: p.y };
            })()}
            initialText={calloutDrawing.pending.text || ''}
            onCommit={calloutDrawing.commitText}
            onCancel={calloutDrawing.cancelText}
          />
        )}
        {renderOverlay &&
          hasInitializedView &&
          renderOverlay({
            width: canvasSize.width,
            height: canvasSize.height,
            pan,
            scale,
            rotation,
          })}
      </div>
    </div>
  );
};

export default UVFloorPlanCanvas;
